import { useState, type ReactNode } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  businessType,
  monthlyVolumeEstimate,
  onboardingStartInput,
  usStateCode,
  type OnboardingStartInput,
} from '@ballast/shared/schemas';
import { submitOnboarding } from '../lib/api.js';

const VOLUME_LABELS: Record<(typeof monthlyVolumeEstimate.options)[number], string> = {
  under_3k: 'Under $3,000',
  between_3k_15k: '$3,000 – $15,000',
  between_15k_50k: '$15,000 – $50,000',
  over_50k: '$50,000+',
};

const BUSINESS_TYPE_LABELS: Record<(typeof businessType.options)[number], string> = {
  cafe: 'Cafe / coffee shop',
  restaurant: 'Restaurant',
  bar: 'Bar / lounge',
  retail: 'Retail',
  salon: 'Salon / personal care',
  service: 'Service business',
  fitness: 'Fitness / wellness',
  professional: 'Professional services',
  other: 'Other',
};

export function OnboardingStartPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingStartInput>({
    resolver: zodResolver(onboardingStartInput),
  });

  const onSubmit: SubmitHandler<OnboardingStartInput> = async (data) => {
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await submitOnboarding(data);
      setSubmittedRef(res.merchantId.slice(0, 8));
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedRef) {
    return (
      <main className="max-w-xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-semibold text-brand-primary mb-4">Thanks — we got it.</h1>
        <p className="text-brand-body mb-2">
          Your reference: <code className="bg-white px-2 py-1 rounded">{submittedRef}</code>
        </p>
        <p className="text-brand-muted">
          A confirmation email is on its way. We&apos;ll follow up within one business day.
        </p>
      </main>
    );
  }

  return (
    <main className="max-w-xl mx-auto px-6 py-16">
      <h1 className="text-3xl font-semibold text-brand-primary mb-2">Get started</h1>
      <p className="text-brand-muted mb-8">
        A few details and we&apos;ll be in touch within one business day.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Field label="Business name" error={errors.businessName?.message}>
          <input
            type="text"
            {...register('businessName')}
            className="input"
            autoComplete="organization"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Your name" error={errors.ownerName?.message}>
            <input type="text" {...register('ownerName')} className="input" autoComplete="name" />
          </Field>
          <Field label="Business type" error={errors.businessType?.message}>
            <select {...register('businessType')} className="input" defaultValue="">
              <option value="" disabled>
                Select...
              </option>
              {businessType.options.map((t) => (
                <option key={t} value={t}>
                  {BUSINESS_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Email" error={errors.ownerEmail?.message}>
            <input
              type="email"
              {...register('ownerEmail')}
              className="input"
              autoComplete="email"
            />
          </Field>
          <Field label="Phone" error={errors.ownerPhone?.message}>
            <input
              type="tel"
              {...register('ownerPhone')}
              className="input"
              autoComplete="tel"
              placeholder="(212) 555-0100"
            />
          </Field>
        </div>

        <Field label="Street address" error={errors.businessAddress?.street?.message}>
          <input
            type="text"
            {...register('businessAddress.street')}
            className="input"
            autoComplete="street-address"
          />
        </Field>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="col-span-2 sm:col-span-2">
            <Field label="City" error={errors.businessAddress?.city?.message}>
              <input
                type="text"
                {...register('businessAddress.city')}
                className="input"
                autoComplete="address-level2"
              />
            </Field>
          </div>
          <Field label="State" error={errors.businessAddress?.state?.message}>
            <select {...register('businessAddress.state')} className="input" defaultValue="">
              <option value="" disabled>
                --
              </option>
              {usStateCode.options.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="ZIP" error={errors.businessAddress?.zip?.message}>
            <input
              type="text"
              {...register('businessAddress.zip')}
              className="input"
              autoComplete="postal-code"
            />
          </Field>
        </div>

        <Field
          label="Estimated monthly stablecoin volume"
          error={errors.monthlyVolumeEstimate?.message}
        >
          <select {...register('monthlyVolumeEstimate')} className="input" defaultValue="">
            <option value="" disabled>
              Select...
            </option>
            {monthlyVolumeEstimate.options.map((v) => (
              <option key={v} value={v}>
                {VOLUME_LABELS[v]}
              </option>
            ))}
          </select>
        </Field>

        {serverError && (
          <p className="text-red-700 text-sm bg-red-50 border border-red-200 rounded p-3">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full sm:w-auto bg-brand-accent hover:brightness-95 disabled:opacity-60 text-white px-8 py-3 rounded-md font-medium transition"
        >
          {submitting ? 'Submitting...' : 'Submit application'}
        </button>
      </form>
    </main>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-brand-body mb-1">{label}</span>
      {children}
      {error && <span className="block text-sm text-red-700 mt-1">{error}</span>}
    </label>
  );
}
